import React, { RefObject, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MoreHorizontal, Flag, EyeOff, X } from 'lucide-react';
import { safeHref } from '../../utils/common/safe-url';
import { AdCreative, recordAdEvent, reportAd, AD_REPORT_REASONS } from '../../api/ads/ads.api';

// Phần dùng chung của mọi chỗ hiện quảng cáo Cab Ads (thẻ quảng cáo, banner trang chủ): đo hiển thị, ghi click,
// ẩn và báo cáo quảng cáo.

// deviceId ổn định (chống gian lận click phía server).
export const getDeviceId = () => {
  try {
    let id = localStorage.getItem('cab-device-id');
    if (!id) {
      id = `d_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('cab-device-id', id);
    }
    return id;
  } catch {
    return undefined;
  }
};

// Danh sách quảng cáo user đã ẩn (giữ giữa các phiên).
const HIDDEN_KEY = 'cab-hidden-ads';
export const getHiddenIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]');
  } catch {
    return [];
  }
};
export const addHiddenId = (id: string) => {
  try {
    const s = new Set(getHiddenIds());
    s.add(id);
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
};

export const IMPRESSION_MIN_RATIO = 0.5;
export const IMPRESSION_MIN_MS = 1000;

/** Ghi HIỂN THỊ đúng 1 lần cho mỗi quảng cáo: phần tử hiện ≥ 50% LIÊN TỤC ≥ 1 giây và tab đang hiển thị. */
export const useAdImpression = (ad: AdCreative | null, ref: RefObject<HTMLElement>) => {
  const impressedRef = useRef(false);
  useEffect(() => {
    if (!ad || !ref.current) return undefined;
    const el = ref.current;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inView = false;
    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const fire = () => {
      timer = null;
      if (impressedRef.current) return;
      impressedRef.current = true;
      recordAdEvent({
        campaignId: ad.campaignId,
        creativeId: ad.creativeId,
        type: 'Impression',
        deviceId: getDeviceId(),
        serveToken: ad.serveToken,
      }).catch(() => undefined);
      cleanup();
    };
    const evaluate = () => {
      if (impressedRef.current) return;
      if (inView && document.visibilityState === 'visible') {
        if (!timer) timer = setTimeout(fire, IMPRESSION_MIN_MS);
      } else {
        clear();
      }
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          inView = e.isIntersecting && e.intersectionRatio >= IMPRESSION_MIN_RATIO;
        });
        evaluate();
      },
      { threshold: [0, IMPRESSION_MIN_RATIO, 1] }
    );
    document.addEventListener('visibilitychange', evaluate);
    function cleanup() {
      clear();
      obs.disconnect();
      document.removeEventListener('visibilitychange', evaluate);
    }
    obs.observe(el);
    return cleanup;
  }, [ad, ref]);
};

/** Ghi CLICK (kèm vé phục vụ) rồi mở link đích ở tab mới. */
export const recordAdClick = (ad: AdCreative) => {
  recordAdEvent({
    campaignId: ad.campaignId,
    creativeId: ad.creativeId,
    type: 'Click',
    deviceId: getDeviceId(),
    serveToken: ad.serveToken,
  }).catch(() => undefined);
  if (ad.landingUrl) window.open(safeHref(ad.landingUrl), '_blank', 'noopener,noreferrer');
};

interface AdOptionsMenuProps {
  ad: AdCreative;
  /** Người xem đã ẩn hoặc báo cáo quảng cáo này (đã ghi vào danh sách ẩn). */
  onHidden: () => void;
  buttonClassName?: string;
}

/**
 * Nút "..." của quảng cáo: Báo cáo quảng cáo và Ẩn quảng cáo này. Mọi click bên trong bị chặn lại ở đây
 * nên không bao giờ bị tính là click vào quảng cáo.
 */
export const AdOptionsMenu: React.FC<AdOptionsMenuProps> = ({ ad, onHidden, buttonClassName }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState(AD_REPORT_REASONS[0].value);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const hideAd = () => {
    addHiddenId(ad.campaignId);
    setMenuOpen(false);
    onHidden();
  };

  const submitReport = async () => {
    try {
      setSending(true);
      const res = await reportAd({
        campaignId: ad.campaignId,
        creativeId: ad.creativeId,
        reason,
        note: note.trim() || undefined,
      });
      toast.success(res.message || 'Đã gửi báo cáo. Cảm ơn bạn!');
      addHiddenId(ad.campaignId);
      setReportOpen(false);
      onHidden();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gửi báo cáo thất bại.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label="Tuỳ chọn quảng cáo"
        onClick={() => setMenuOpen((v) => !v)}
        className={
          buttonClassName ||
          'p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-[#657384] dark:text-slate-300'
        }
      >
        <MoreHorizontal size={16} />
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-52 rounded-lg border border-[#DEE0E6] dark:border-slate-700 bg-white dark:bg-dark2 shadow-lg py-1 text-sm">
            <button
              className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2 ui-text"
              onClick={() => {
                setMenuOpen(false);
                setReportOpen(true);
              }}
            >
              <Flag size={14} /> Báo cáo quảng cáo
            </button>
            <button
              className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2 ui-text"
              onClick={hideAd}
            >
              <EyeOff size={14} /> Ẩn quảng cáo này
            </button>
          </div>
        </>
      )}

      {/* Dialog báo cáo */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4 cursor-default"
          onClick={() => setReportOpen(false)}
        >
          <div className="w-full max-w-sm rounded-xl bg-white dark:bg-dark2 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold ui-text">Báo cáo quảng cáo</h3>
              <button onClick={() => setReportOpen(false)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10">
                <X size={18} className="ui-text" />
              </button>
            </div>
            <p className="text-xs ui-muted mb-3">Vì sao bạn báo cáo quảng cáo này?</p>
            <div className="space-y-1.5 mb-3">
              {AD_REPORT_REASONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm ui-text cursor-pointer">
                  <input
                    type="radio"
                    name="ad-report-reason"
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            <textarea
              className="w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none resize-none ui-text"
              rows={2}
              placeholder="Mô tả thêm (tuỳ chọn)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              onClick={submitReport}
              disabled={sending}
              className="mt-3 w-full rounded-lg bg-[#DC2626] text-white text-sm font-medium py-2 disabled:opacity-50"
            >
              {sending ? 'Đang gửi…' : 'Gửi báo cáo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
